/**
 * Chat WebSocket service — socket.io-client v4 integration for the CRM.
 *
 * Architecture (Chat Decision #2, Phase F):
 *   - WS is used for real-time receive: chat:message, chat:typing, chat:presence,
 *     chat:messageEdited, chat:messageDeleted, chat:reactionAdded, chat:reactionRemoved
 *   - REST POST /chat/rooms/:roomId/messages stays for sending (existing validation path)
 *   - The gateway lives in the `/ws` namespace (the default namespace has no handlers) and
 *     authenticates the handshake with the 60-second token from POST /auth/ws-token.
 *     socket.io's own reconnection would replay that token and fail auth every time, so
 *     reconnection is ours: each attempt mints a fresh token.
 *   - Feature flag: CHAT_WS_ENABLED checked by the backend at connection time
 *
 * Usage:
 *   import chatSocket from './chat-socket';
 *   chatSocket.connect();                       // call once on mount
 *   chatSocket.joinRoom(roomId);                // when opening a room (re-joined after a reconnect)
 *   chatSocket.leaveRoom(roomId);               // when leaving a room
 *   chatSocket.emitTyping(roomId);              // throttled, call on input change
 *   chatSocket.emitMarkRead(roomId, messageId); // id of the newest message the user has seen
 *   chatSocket.on('chat:message', handler);     // survives reconnects; safe before connect()
 *   chatSocket.disconnect();                    // call on logout / unmount
 *
 * Besides the gateway events, `on` also delivers 'connected' (the gateway accepted the
 * handshake), 'disconnect' and 'connect_error'.
 *
 * Story reference: Epic 25 / 25-4 (CRM socket.io-client integration)
 */

import { io } from 'socket.io-client';
import { apiClient } from './client';

// Resolve the backend WS URL.  In proxy mode REACT_APP_API_URL is empty and
// socket.io connects to the same host the CRA dev server is proxying.
const rawBase = process.env.REACT_APP_API_URL || '';
const WS_URL = rawBase ? rawBase.replace(/\/api\/v1\/?$/, '') : window.location.origin;
const WS_NAMESPACE = '/ws';

/** Typing debounce: stop broadcasting typing events more than once per 500 ms */
const TYPING_DEBOUNCE_MS = 500;

/** Reconnect backoff: 1 s doubling up to 30 s, for as long as the page wants the socket */
const RECONNECT_BASE_MS = 1000;
const RECONNECT_MAX_MS = 30000;

/** A fresh handshake token, or null when the API would not issue one. */
async function fetchWsToken() {
  try {
    const { data } = await apiClient.post('/auth/ws-token');
    return typeof data?.token === 'string' && data.token ? data.token : null;
  } catch (err) {
    console.warn('[chat-socket] ws-token failed:', err?.message);
    return null;
  }
}

class ChatSocketService {
  constructor() {
    this._socket = null;
    this._ready = false;          // the gateway accepted the handshake ('connected')
    this._wanted = false;         // connect() was called and disconnect() has not been since
    this._opening = false;        // a token fetch is under way
    this._attempt = 0;            // retries since the gateway last accepted us
    this._seq = 0;                // lets disconnect() overtake a token fetch in flight
    this._reconnectTimer = null;
    this._handlers = new Map();   // event -> Set<handler>, bound again to every new socket
    this._typingTimers = {};      // roomId -> timer id
    this._currentRooms = new Set();
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  /**
   * Connect to the backend gateway.  Safe to call multiple times — a socket that is
   * connected, still connecting, or waiting for its next retry counts as the connection.
   */
  connect() {
    this._wanted = true;
    if (this._socket || this._opening || this._reconnectTimer) return;
    this._open();
  }

  /** Disconnect and clean up.  Call on logout or component unmount. */
  disconnect() {
    this._wanted = false;
    this._seq += 1;               // a token fetch still in flight must not open a socket nobody holds
    this._opening = false;
    this._attempt = 0;
    clearTimeout(this._reconnectTimer);
    this._reconnectTimer = null;
    this._dropSocket();
    this._currentRooms.clear();
    Object.values(this._typingTimers).forEach(clearTimeout);
    this._typingTimers = {};
  }

  async _open() {
    const seq = ++this._seq;
    this._opening = true;
    const token = await fetchWsToken();
    if (seq !== this._seq) return;
    this._opening = false;
    if (!token) {
      this._scheduleReconnect();
      return;
    }

    const socket = io(`${WS_URL}${WS_NAMESPACE}`, {
      path: '/socket.io',
      transports: ['websocket'],
      auth: { token },
      reconnection: false,
      timeout: 10000,
    });
    this._socket = socket;

    // The service's own listeners go first, so `_ready` is set before an app handler runs.
    // The room joins wait for 'connected': the gateway only learns who we are after the
    // transport is up, and a joinRoom sent earlier can be refused as unauthenticated.
    socket.on('connected', () => {
      this._ready = true;
      this._attempt = 0;
      for (const roomId of this._currentRooms) {
        socket.emit('joinRoom', { roomId });
      }
    });
    socket.on('disconnect', (reason) => this._onDown(socket, reason));
    socket.on('connect_error', (err) => {
      console.warn('[chat-socket] connect error:', err.message);
      this._onDown(socket, 'connect_error');
    });
    socket.on('error', (payload) => {
      console.warn('[chat-socket] gateway error:', payload?.code || payload);
    });

    this._handlers.forEach((set, event) => {
      set.forEach((handler) => socket.on(event, handler));
    });
  }

  _onDown(socket, reason) {
    if (this._socket !== socket) return;      // already replaced or closed by us
    this._dropSocket();
    // "io client disconnect" is us leaving on purpose; anything else is a drop.
    if (reason !== 'io client disconnect') this._scheduleReconnect();
  }

  _dropSocket() {
    const socket = this._socket;
    this._socket = null;
    this._ready = false;
    if (socket) socket.disconnect();
  }

  _scheduleReconnect() {
    if (!this._wanted || this._reconnectTimer) return;
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** this._attempt, RECONNECT_MAX_MS);
    this._attempt += 1;
    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null;
      if (this._wanted && !this._socket && !this._opening) this._open();
    }, delay);
  }

  // ─── Room management ────────────────────────────────────────────────────────

  /**
   * Join a room.  The gateway validates membership server-side.  Rooms asked for while
   * the socket is down are joined as soon as the gateway accepts it.
   * @param {string} roomId
   */
  joinRoom(roomId) {
    if (!roomId) return;
    this._currentRooms.add(roomId);
    if (this._ready) this._socket.emit('joinRoom', { roomId });
  }

  /**
   * Leave a room.  Stops the gateway from sending events for this room to
   * this socket.
   * @param {string} roomId
   */
  leaveRoom(roomId) {
    if (!roomId) return;
    this._currentRooms.delete(roomId);
    if (this._ready) this._socket.emit('leaveRoom', { roomId });
    // Cancel pending typing timer for this room
    if (this._typingTimers[roomId]) {
      clearTimeout(this._typingTimers[roomId]);
      delete this._typingTimers[roomId];
    }
  }

  // ─── Client-emitted events ──────────────────────────────────────────────────

  /**
   * Emit a typing indicator for the room.  At most one event per TYPING_DEBOUNCE_MS,
   * however fast the user types.
   * @param {string} roomId
   */
  emitTyping(roomId) {
    if (!this._ready || !roomId) return;
    if (this._typingTimers[roomId]) return;

    this._socket.emit('typing', { roomId, isTyping: true });

    this._typingTimers[roomId] = setTimeout(() => {
      delete this._typingTimers[roomId];
    }, TYPING_DEBOUNCE_MS);
  }

  /**
   * Mark everything in a room up to a message as read.  The gateway batch-updates
   * Message.readBy for all messages created at or before that message and tells the
   * room who has seen them.
   *
   * @param {string} roomId
   * @param {string} lastSeenMessageId  id of the newest message the user has seen
   * @returns {boolean} whether the event went out (false while the socket is down, so the
   *   caller can try again once it is back)
   */
  emitMarkRead(roomId, lastSeenMessageId) {
    if (!this._ready || !roomId || !lastSeenMessageId) return false;
    this._socket.emit('markRead', { roomId, lastSeenMessageId });
    return true;
  }

  // ─── Event subscription passthrough ─────────────────────────────────────────

  /**
   * Subscribe to a socket.io event.  The subscription outlives any one socket: it is
   * bound again after every reconnect, and may be made before connect().
   * @param {string} event
   * @param {function} handler
   */
  on(event, handler) {
    let set = this._handlers.get(event);
    if (!set) {
      set = new Set();
      this._handlers.set(event, set);
    }
    if (set.has(handler)) return;
    set.add(handler);
    if (this._socket) this._socket.on(event, handler);
  }

  /**
   * Unsubscribe from a socket.io event.
   * @param {string} event
   * @param {function} handler
   */
  off(event, handler) {
    const set = this._handlers.get(event);
    if (set) {
      set.delete(handler);
      if (set.size === 0) this._handlers.delete(event);
    }
    if (this._socket) this._socket.off(event, handler);
  }

  /** Whether the gateway has accepted the connection. */
  get connected() {
    return this._ready;
  }
}

// Singleton — one connection shared across all CRM components
const chatSocket = new ChatSocketService();
export default chatSocket;
